import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Sparkles, RefreshCw, AlertTriangle, AlertCircle, Info,
  Radio, Gavel, UserCheck, FlaskConical, Globe, Brain, ArrowRight,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SEVERITY = {
  CRITICAL: { color: "#FF3B30", bg: "#FF3B30", Icon: AlertTriangle },
  HIGH:     { color: "#FF9F0A", bg: "#FF9F0A", Icon: AlertCircle },
  MEDIUM:   { color: "#00E5FF", bg: "#00E5FF", Icon: Info },
};

const CATEGORY_ICON = {
  intelligence: Brain,
  event: Radio,
  tender: Gavel,
  hr: UserCheck,
  patent: FlaskConical,
  warroom: Globe,
};

export default function DailyInsight({ orientation = "horizontal" }) {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (force = false) => {
    setError("");
    if (force) setRegenerating(true); else setLoading(true);
    const attempt = async () => axios.get(`${API}/insights/daily`, {
      params: { lang, force: force ? "true" : "false" },
      withCredentials: true,
      timeout: 90000,
    });
    try {
      let res;
      try {
        res = await attempt();
      } catch (err) {
        // Cold-start LLM 502 → retry once after 2s
        if (err?.response?.status === 502) {
          await new Promise(r => setTimeout(r, 2000));
          res = await attempt();
        } else {
          throw err;
        }
      }
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "error");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, [lang]);

  useEffect(() => { load(false); }, [load]);

  const insights = data?.insights || [];
  const busy = loading || regenerating;

  return (
    <div
      data-testid="daily-insight-widget"
      className="relative bg-[#0D0F12] border border-white/[0.04] p-5 overflow-hidden h-full"
      style={{ borderRadius: "2px" }}
    >

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4 relative">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#00E5FF]/12" style={{ borderRadius: "2px" }}>
            <Sparkles className="w-4 h-4 text-[#00E5FF]" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#00E5FF] font-bold">
              {t("insight.title")}
            </p>
            <p className="text-[10px] text-[#A0AEC0] tracking-wide mt-0.5">
              {t("insight.subtitle")}
            </p>
          </div>
        </div>
        <button
          data-testid="insight-refresh-btn"
          onClick={() => load(true)}
          disabled={busy}
          title={t("insight.regenerate")}
          className="flex items-center gap-1.5 text-[10px] text-[#00E5FF] hover:text-white px-2.5 py-1.5 tracking-widest uppercase transition-colors disabled:opacity-40"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          {t("insight.regenerate")}
        </button>
      </div>

      {/* Content */}
      {busy && insights.length === 0 && (
        <div className="flex items-center justify-center gap-3 py-8 text-xs text-[#CBD5E1] tracking-widest uppercase">
          <div className="w-4 h-4 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
          {t("insight.generating")}
        </div>
      )}

      {!busy && insights.length === 0 && !error && (
        <div className="text-center py-6 text-xs text-[#A0AEC0] tracking-widest uppercase">
          {t("insight.empty")}
        </div>
      )}

      {error && (
        <div className="text-center py-6 text-xs text-[#FF3B30] tracking-widest uppercase">
          {error}
        </div>
      )}

      {insights.length > 0 && (
        <div
          data-testid="insights-container"
          className={
            orientation === "vertical"
              ? "flex flex-col gap-3 max-h-[440px] overflow-y-auto pr-1"
              : "grid grid-cols-1 md:grid-cols-3 gap-3"
          }
        >
          {insights.map((ins, i) => {
            const sev = SEVERITY[(ins.severity || "MEDIUM").toUpperCase()] || SEVERITY.MEDIUM;
            const CatIcon = CATEGORY_ICON[ins.category] || Brain;
            return (
              <div
                key={ins.id || ins.title || `ins-${i}`}
                data-testid={`insight-card-${i}`}
                className="bg-[#050505] rounded-md p-3 hover:bg-[#0A0C10] transition-colors flex flex-col"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span
                    className="flex items-center gap-1 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-sm"
                    style={{ color: sev.color, backgroundColor: `${sev.color}20` }}
                  >
                    <sev.Icon className="w-2.5 h-2.5" />
                    {ins.severity || "MEDIUM"}
                  </span>
                  <CatIcon className="w-3.5 h-3.5 text-[#A0AEC0]" />
                </div>
                <p className="text-xs text-white font-semibold leading-snug mb-1.5">
                  {ins.title}
                </p>
                <p className="text-[11px] text-[#CBD5E1] leading-relaxed mb-3 flex-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  {ins.description}
                </p>
                {ins.action && (
                  <div className="flex items-start gap-1.5 pt-2">
                    <ArrowRight className="w-3 h-3 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                    <span className="text-[10px] text-[#00E5FF] leading-snug">{ins.action}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {data?.generated_at && (
        <div className="flex items-center justify-between mt-3 pt-3 text-[9px] text-[#A0AEC0] tracking-widest uppercase">
          <span>{t("insight.updated")}: {new Date(data.generated_at).toLocaleString(lang === "en" ? "en-US" : "it-IT", { dateStyle: "medium", timeStyle: "short" })}</span>
          <span className="flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> {t("insight.powered")}</span>
        </div>
      )}
    </div>
  );
}
